import React, { useState, useContext } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Modal,
  Typography, 
  Button, 
  Progress, 
  Empty, 
  Space, 
  message,
  Avatar,
  Popover,
  Descriptions,
  Tag,
} from 'antd';
import { 
  BookOutlined, 
  PlayCircleOutlined, 
  ClockCircleOutlined,
  StarOutlined,
  ArrowRightOutlined,
  UserOutlined,
  LinkedinOutlined,
  EyeOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useEnrolledCourses from 'PlatForms/SelfLMS/Hooks/Courses/useEnrolledCourses';
import { selfLearnerService } from 'PlatForms/SelfLMS/Services/Courses/CourseService';
import { ProfileContext } from 'Common/hooks/context/ProfileContext';

const { Title, Text, Paragraph } = Typography;

const EnrolledCoursesCard = () => {
  const navigate = useNavigate();
  const { enrollments, loading, error,  handlePageChange } = useEnrolledCourses();

  const [startingCourse, setStartingCourse] = useState(null);
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  
  
  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedPriceData, setSelectedPriceData] = useState(null);
  
  const displayedCourses = enrollments.slice(0, 6);

    const openPriceModal = (e, course) => {
    e.stopPropagation(); 
    setSelectedPriceData(course);
    setIsModalVisible(true);
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";
    const [hours, minutes] = timeString.split(":");
    const hoursInt = parseInt(hours);
    const minutesInt = parseInt(minutes);
    if (hoursInt === 0 && minutesInt === 0) return "Not specified";
    if (hoursInt === 0) return `${minutesInt}m`;
    if (minutesInt === 0) return `${hoursInt}h`;
    return `${hoursInt}h ${minutesInt}m`;
  };

  const getAuthorName = (course) => {
    if (course.courseAuthor && course.courseAuthor.name) return course.courseAuthor.name;
    if (course.authorName) return course.authorName;
    return "Unknown Author";
  };

  const getLinkedInUrl = (course) => {
    if (course.courseAuthor && course.courseAuthor.linkedinUrl) return course.courseAuthor.linkedinUrl;
    if (course.linkedinUrl) return course.linkedinUrl;
    if (course.authorLinkedIn) return course.authorLinkedIn;
    return null;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const toggleDescription = (courseId, e) => {
    e.stopPropagation();
    setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
  };

  const handleContinueLearning = async (enrollmentId) => {
    if (isBlocked) {
      message.warning("Please complete your profile first");
      return;
    }
    try {
      setStartingCourse(enrollmentId);
      const response = await selfLearnerService.startCourseEnrollment(enrollmentId);
      if (response.isAssessmentPage) {
        navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${response.moduleProgressId}/assessment`, {
          state: {
            moduleId: response.moduleId,
            assessmentId: response.assessmentPage.id,
            moduleProgressId: response.moduleProgressId,
            enrollmentId: enrollmentId,
            assessmentTitle: response.assessmentPage.title,
            assessmentData: response
          }
        });
      } else {
        let pageId = response.currentPageId || response.previousPageId || response.nextPageId || response.moduleId;
        navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${response.moduleProgressId}/pages/${pageId}`, {
          state: { moduleId: response.moduleId }
        });
      }
    } catch (error) {
      console.error('Error starting course:', error);
      message.error('Failed to start course. Please try again.');
    } finally {
      setStartingCourse(null);
    }
  };

  const handleExploreNewCourses = () => navigate('/selfLearner-dashboard/available-courses');
  const handleViewAllEnrolledCourses = () => navigate('/selfLearner-dashboard/enrolled-courses');

  if (loading) {
    return (
      <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)', marginBottom: '16px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="loading-spinner" />
          <Text style={{ display: 'block', marginTop: '12px', color: '#8c8c8c' }}>Loading your courses...</Text>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)', marginBottom: '16px' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<div><Title level={5} style={{ color: '#ff4d4f', marginBottom: '4px' }}>Error Loading Courses</Title><Text type="secondary">Please try refreshing</Text></div>} style={{ padding: '30px 0' }}>
          <Button type="primary" onClick={() => window.location.reload()}>Refresh Page</Button>
        </Empty>
      </Card>
    );
  }

  return (
    <Card 
          style={{ borderRadius: '16px', border: 'none', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)', overflow: 'hidden', marginBottom: '16px' }}
          bodyStyle={{ padding: '0' }}
        >
      {/* Header Section - Reduced padding */}
      <div style={{ padding: "20px 24px", background: "#f5f9ff", borderBottom: "1px solid #dce7ff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <Space align="center" size="middle">
            <BookOutlined style={{ fontSize: 28, color: "#1677ff" }} />
            <div>
              <Title level={4} style={{ margin: 0, color: "#1f2937", fontWeight: 700 }}>My Courses</Title>
              <Text style={{ color: "#6b7280", fontSize: '13px' }}>Track your progress</Text>
            </div>
          </Space>
          <Button type="primary" icon={<StarOutlined />} onClick={handleExploreNewCourses} style={{ borderRadius: 6 }}>
            Explore New
          </Button>
        </div>
      </div>

      <div style={{ padding: '20px' }}>
        {enrollments.length === 0 ? (
          <Empty image={<BookOutlined style={{ fontSize: '32px', color: '#bfbfbf' }} />} description={<Text>No enrolled courses yet</Text>} style={{ padding: '40px 0' }}>
            <Button type="primary" onClick={handleExploreNewCourses}>Browse Courses</Button>
          </Empty>
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {displayedCourses.map((enrollment) => {
                const progressPercentage = enrollment.isCompleted ? 100 : Math.round(((enrollment.completedModuleCount || 0) / (enrollment.moduleCount || 1)) * 100);
                const linkedInUrl = getLinkedInUrl(enrollment.course);
                const authorName = getAuthorName(enrollment.course);
                
                return (
                  <Col xs={24} sm={12} md={8} key={enrollment.id}>
                    <Card
                      hoverable
                  
                      style={{
    height: "100%",
    minHeight: 320, // Slightly increased to give breathing room
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden"
  }}
                     bodyStyle={{
    padding: "16px",
    flex: 1, // This allows the body to fill the card
    display: "flex",
    flexDirection: "column"
  }}                    >
                 
                        
                          {/* Price Banner */}
                        <div 
                          onClick={(e) => openPriceModal(e, enrollment.course)}
                          style={{
                            position: "absolute",
                            top: 15,
                            right: -35,
                            background: enrollment.course?.isPaid ? "#13c2c2" : "#52c41a", 
                            color: "white",
                            padding: "2px 40px",
                            transform: "rotate(45deg)",
                            fontSize: "11px",
                            fontWeight: "bold",
                            zIndex: 10,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            textAlign: "center",
                            width: "120px",
                            cursor: "pointer"
                          }}
                        > 
                          {enrollment.course?.isPaid ? `${enrollment.course.pricing?.currency   === "USD" ? "$":enrollment.course.pricing?.currency  }${enrollment.course.pricing?.price || 0}` : "FREE"}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                            <Space direction="vertical" size="middle" style={{ width: "100%", flex: 1 }}>
                          
                           {/* 1. Status and Title (Fixed Height) */}
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start", minHeight: "80px" }}>
                                                              
                                <Title
                                  level={4}
                                  style={{
                                    margin: 0,
                                    paddingRight: 45, 
                                    fontSize: "16px",
                                    lineHeight: "22px",
                                    height: "44px", // Fixed 2-line height
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical"
                                  }}
                                >
                                  {enrollment.course?.title }
                                </Title>
                              </div>



                        
<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
  <Avatar size={18} icon={<UserOutlined />} />
  <Popover
    content={
      <div style={{ padding: '4px' }}>
        {authorName}
        {linkedInUrl && (
          <a href={linkedInUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}>
            <LinkedinOutlined />
          </a>
        )}
      </div>
    }
    trigger="click"
  >
    <Text
      style={{
        fontSize: "12px",
        color: "#595959",   
        cursor: "pointer",
        textDecoration: "underline dotted"
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {authorName}
    </Text>
  </Popover>
</div>

                      

                      {/* Card Content - Compact */}
                      <div style={{ padding: '16px' }}>
                        {enrollment.course?.description && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                             <Paragraph
    style={{
      fontSize: 12,
      lineHeight: "18px",
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      marginBottom: 4
    }}
  >                                {expandedCourseId === enrollment.id ? enrollment.course.description : truncateText(enrollment.course.description, 48)}
                              </Paragraph>
                              <Button type="link" size="small" onClick={(e) => toggleDescription(enrollment.id, e)} style={{ padding: '0', height: 'auto', fontSize: '11px' }}>
                                {expandedCourseId === enrollment.id ? "See less" : "See more"}
                              </Button>
                            </div>
                          </div>
                        )}

                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Progress</Text>
                            <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>{progressPercentage}%</Text>
                          </div>
                          <Progress percent={progressPercentage} strokeColor={enrollment.isCompleted ? '#52c41a' : '#1890ff'} strokeWidth={4} showInfo={false} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>{enrollment.completedModuleCount || 0}/{enrollment.moduleCount || 0} Modules</Text>
                            <Text type="secondary" style={{ fontSize: '10px' }}><ClockCircleOutlined /> {formatTime(enrollment.course?.estimatedTime)}</Text>
                          </div>
                        </div>

        
                 
                
                        <Button
                          type={enrollment.isCompleted || enrollment.isBlocked ? "default" : "primary"}
                          size="middle"
                          icon={<PlayCircleOutlined />}
                          onClick={() => handleContinueLearning(enrollment.id)}
                          loading={startingCourse === enrollment.id}
                          disabled={enrollment.isBlocked || isBlocked}
                          block
                          style={{ height: '36px', borderRadius: '6px', fontSize: '13px' }}
                        >
                          {enrollment.isCompleted ? "Review" : enrollment.isBlocked ? "Blocked" : "Continue"}
                        </Button>
                      </div>
                      </Space>

</div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
{enrollments.length > 0 && (
  <div style={{ marginTop: '24px', textAlign: 'center' }}>
    
    <Button
      type="primary"
      ghost
      icon={<ArrowRightOutlined />}
      onClick={handleViewAllEnrolledCourses}
      style={{ marginTop: 12, height: '40px', borderRadius: '8px' }}
    >
      View All Enrollments
    </Button>
  </div>
)}

          </>
        )}
      </div>
         {/* Price Detail Modal */}
      <Modal
        title="Course Price Details"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        {selectedPriceData && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Course">{selectedPriceData.title}</Descriptions.Item>
            <Descriptions.Item label="Price">
              {selectedPriceData.isPaid ? (
                <Text strong>
                  {selectedPriceData.pricing?.currency || "$"}{selectedPriceData.pricing?.price?.toFixed(2) || "0.00"}
                </Text>
              ) : (
                <Text strong style={{ color: "#52c41a" }}>FREE</Text>
              )}
            </Descriptions.Item>
            {selectedPriceData.isPaid && selectedPriceData.pricing?.accessDurationDays && (
              <Descriptions.Item label="Access Duration">
                {selectedPriceData.pricing.accessDurationDays} days
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
};

export default EnrolledCoursesCard;